import React from 'react';
import { useTranslation } from 'react-i18next';

// Deterministic avatar background from the donor's first character. Shared
// with PublicCampaign (was a private helper there) so the canvas preview and
// the public page colour avatars identically.
export function avatarColor(name: string, primary: string): string {
  const colors = [primary, '#10B981', '#F59E0B', '#EF4444', '#8B5CF6', '#EC4899', '#06B6D4'];
  const idx = (name.charCodeAt(0) || 0) % colors.length;
  return colors[idx];
}

// One already-resolved donor row. Both call sites do their own data prep
// (i18n name resolution, time formatting, extra-field lookup) and hand the
// component plain strings — this component is purely presentational so the
// canvas and the public page can never visually drift.
export interface RecentDonorView {
  name: string;            // raw donor name ('' → rendered as the anonymous label)
  amount: number;
  ambassadorName?: string; // '' / undefined → no "via" line for this donor
  extraLabel?: string;     // optional extra form field label (already resolved)
  extraValue?: string;     // optional extra form field value
  timeLabel?: string;      // already formatted, e.g. "לפני 5 דקות"
}

// Per-element typography override. Any field left undefined falls back to the
// element's built-in default.
export interface DonorTypo {
  font?: string;
  size?: number;
  color?: string;
  weight?: number | string;
}

export interface RecentDonorsTypo {
  title?: DonorTypo;
  name?: DonorTypo;
  amount?: DonorTypo;
  ambassador?: DonorTypo;
  time?: DonorTypo;
  extra?: DonorTypo;
}

// Flatten the block's flat props (titleFont, nameSize, amountColor, …) into the
// structured typo object the component consumes. Kept here so both call sites
// share one mapping.
export function donorTypoFromProps(p: any): RecentDonorsTypo {
  const el = (prefix: string): DonorTypo => ({
    font: p[`${prefix}Font`] || undefined,
    size: p[`${prefix}Size`] ?? undefined,
    color: p[`${prefix}Color`] || undefined,
    weight: p[`${prefix}Weight`] ?? undefined,
  });
  return {
    title: el('title'),
    name: el('name'),
    amount: el('amount'),
    ambassador: el('ambassador'),
    time: el('time'),
    extra: el('extra'),
  };
}

export interface RecentDonorsBlockProps {
  donors: RecentDonorView[];
  title?: string;
  primary: string;
  locale: string;                 // 'he' | 'en' — drives i18n labels + dir
  showAmount?: boolean;
  showAmbassador?: boolean;       // block-level: reserve the ambassador line for equal height
  hasExtraField?: boolean;        // block-level: reserve the extra-field line for equal height
  displayStyle?: 'list' | 'grid' | 'cards';
  titleColor?: string;            // fallback title colour (block style textColor)
  typo?: RecentDonorsTypo;
}

const num = (v: number | undefined, def: number) => (v == null ? def : v);

const RecentDonorsBlock: React.FC<RecentDonorsBlockProps> = ({
  donors,
  title,
  primary,
  locale,
  showAmount = true,
  showAmbassador = false,
  hasExtraField = false,
  displayStyle = 'list',
  titleColor,
  typo,
}) => {
  const { t } = useTranslation();
  const tl = (key: string, opts?: Record<string, any>) => t(key, { lng: locale, ...(opts || {}) }) as string;
  const dir = locale === 'en' ? 'ltr' : 'rtl';

  // Resolve each element's effective style (override → default).
  const elStyle = (key: keyof RecentDonorsTypo, def: { size: number; color: string; weight: number | string }): React.CSSProperties => {
    const cfg = typo?.[key] || {};
    return {
      fontFamily: cfg.font || undefined,
      fontSize: num(cfg.size, def.size),
      color: cfg.color || def.color,
      fontWeight: (cfg.weight ?? def.weight) as any,
    };
  };

  const titleStyle = elStyle('title', { size: 18, weight: 800, color: titleColor || '#1f2937' });
  const nameStyle = elStyle('name', { size: 14, weight: 600, color: '#374151' });
  const amountStyle = elStyle('amount', { size: 14, weight: 800, color: primary });
  const ambStyle = elStyle('ambassador', { size: 12, weight: 600, color: '#4b5563' });
  const timeStyle = elStyle('time', { size: 12, weight: 400, color: '#9ca3af' });
  const extraStyle = elStyle('extra', { size: 12, weight: 400, color: '#4b5563' });

  // Optional lines render as reserved "slots": when the block enables a line
  // (showAmbassador / hasExtraField) every row renders it, falling back to a
  // visibility:hidden placeholder of identical height when a given donor has
  // no value. So a direct donation is exactly as tall as one via an ambassador
  // — equal height by construction, with no pixel estimation.
  const AmbassadorSlot: React.FC<{ name?: string }> = ({ name }) => (
    <div
      aria-hidden={name ? undefined : true}
      style={{ ...ambStyle, marginTop: 2, display: 'flex', alignItems: 'center', gap: 4, visibility: name ? 'visible' : 'hidden' }}
    >
      <span aria-hidden>🤝</span>
      <span style={{ color: '#9ca3af', fontFamily: ambStyle.fontFamily, fontSize: ambStyle.fontSize }}>{tl('public.recentDonors.viaAmbassador')} </span>
      <span style={{ fontWeight: ambStyle.fontWeight, color: ambStyle.color }}>{name || '—'}</span>
    </div>
  );

  const ExtraSlot: React.FC<{ label?: string; value?: string }> = ({ label, value }) => (
    <div
      aria-hidden={value ? undefined : true}
      style={{ ...extraStyle, marginTop: 2, visibility: value ? 'visible' : 'hidden' }}
    >
      {label && <span style={{ color: '#9ca3af' }}>{label}: </span>}
      <span>{value || '—'}</span>
    </div>
  );

  const TimeSlot: React.FC<{ label?: string; block?: boolean }> = ({ label, block }) => {
    const style: React.CSSProperties = { ...timeStyle, visibility: label ? 'visible' : 'hidden', display: block ? 'block' : undefined };
    return <span style={style} aria-hidden={label ? undefined : true}>{label || '—'}</span>;
  };

  const Avatar: React.FC<{ name: string; size: number }> = ({ name, size }) => {
    const firstChar = (name.trim()[0] || '?').toUpperCase();
    return (
      <div style={{
        width: size, height: size, borderRadius: '50%',
        background: avatarColor(name.trim() || '?', primary),
        color: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontWeight: 'bold', fontSize: Math.round(size * 0.35), flexShrink: 0,
      }}>
        {firstChar}
      </div>
    );
  };

  // A single donor as a horizontal row (used by list + grid). Every enabled
  // line is always rendered (real or hidden spacer) → all rows equal height.
  const Row: React.FC<{ d: RecentDonorView }> = ({ d }) => {
    const displayName = d.name.trim() || tl('public.recentDonors.anonymousDonor');
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, background: '#f9fafb', borderRadius: 12, padding: '10px 14px', border: '1px solid #f3f4f6' }}>
        <Avatar name={d.name} size={40} />
        <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8 }}>
            <span style={{ ...nameStyle, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</span>
            {showAmount && <span style={amountStyle}>₪{(d.amount || 0).toLocaleString()}</span>}
          </div>
          {hasExtraField ? <ExtraSlot label={d.extraLabel} value={d.extraValue} /> : null}
          {showAmbassador ? <AmbassadorSlot name={d.ambassadorName} /> : null}
          <TimeSlot label={d.timeLabel} />
        </div>
      </div>
    );
  };

  // A single donor as a centred card (used by cards). Fixed width + reserved
  // lines → all cards identical regardless of which lines carry data.
  const Card: React.FC<{ d: RecentDonorView }> = ({ d }) => {
    const displayName = d.name.trim() || tl('public.recentDonors.anonymousDonor');
    return (
      <div style={{
        background: '#f9fafb', borderRadius: 10, padding: 14, border: '1px solid #f3f4f6',
        width: 160, flexShrink: 0,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 6, textAlign: 'center',
      }}>
        <Avatar name={d.name} size={44} />
        <div style={{ ...nameStyle, maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{displayName}</div>
        {showAmount && <div style={amountStyle}>₪{(d.amount || 0).toLocaleString()}</div>}
        {hasExtraField ? <ExtraSlot label={d.extraLabel} value={d.extraValue} /> : null}
        {showAmbassador ? <AmbassadorSlot name={d.ambassadorName} /> : null}
        <TimeSlot label={d.timeLabel} block />
      </div>
    );
  };

  return (
    <div dir={dir}>
      {title ? <h3 style={{ ...titleStyle, marginBottom: 14, textAlign: 'center' }}>{title}</h3> : null}
      {donors.length === 0 ? (
        <div style={{ textAlign: 'center', padding: '16px 12px', color: '#9ca3af', fontSize: 14 }}>
          <span style={{ fontSize: 28, display: 'block', marginBottom: 6 }}>💝</span>
          {tl('public.recentDonors.empty')}
        </div>
      ) : displayStyle === 'cards' ? (
        <div style={{ display: 'flex', gap: 10, overflowX: 'auto', paddingBottom: 4 }}>
          {donors.map((d, i) => <Card key={i} d={d} />)}
        </div>
      ) : displayStyle === 'grid' ? (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 10 }}>
          {donors.map((d, i) => <Row key={i} d={d} />)}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {donors.map((d, i) => <Row key={i} d={d} />)}
        </div>
      )}
    </div>
  );
};

export default RecentDonorsBlock;
